import { useId } from 'react';

import { Button, type ButtonProps } from '@/ui/primitives/button';

import { textoEspera, useEspera } from './espera';

export interface BotaoComEsperaProps extends ButtonProps {
  /** Antes deste instante (ISO) o botão fica bloqueado, com a contagem ao lado. */
  liberadoEm: string | null;
  /** Fuso da hora mostrada ("Liberado às 13:05"). */
  fuso?: string;
  /** Outro motivo de bloqueio, escrito ao lado (ex.: "Captura desligada"). */
  motivo?: string | null;
  /**
   * `false` quando o motivo já está escrito por perto (ex.: o selo da situação): ele continua como
   * descrição acessível do botão, só sai da vista. A contagem da espera sempre aparece.
   */
  motivoVisivel?: boolean;
  /** Anunciado (aria-live) quando a espera acaba com o botão na tela. */
  avisoLiberado?: string;
}

/** O que mostrar ao lado: o motivo fixo vence; senão, a contagem enquanto espera. */
function useBloqueio(liberadoEm: string | null, motivo: string | null, fuso?: string) {
  const { restante, liberou } = useEspera(liberadoEm);
  const esperando = liberadoEm !== null && restante > 0;
  const espera = esperando ? textoEspera(liberadoEm, restante, fuso) : null;
  return { texto: motivo ?? espera, bloqueado: esperando || motivo !== null, liberou };
}

function Motivo({ id, texto, oculto }: { id: string; texto: string; oculto: boolean }) {
  return (
    <span id={id} className={oculto ? 'sr-only' : 'tabular text-xs text-texto-suave'}>
      {texto}
    </span>
  );
}

/**
 * Botão que respeita uma espera imposta de fora (SEFAZ, limite de uso): desabilitado com o motivo
 * em texto ao lado, contagem que muda a cada minuto e aviso ao leitor de tela só quando libera.
 */
export function BotaoComEspera({
  liberadoEm,
  fuso,
  motivo,
  motivoVisivel = true,
  avisoLiberado = 'Liberado. Já pode tentar de novo.',
  disabled,
  children,
  ...props
}: BotaoComEsperaProps) {
  const id = useId();
  const { texto, bloqueado, liberou } = useBloqueio(liberadoEm, motivo ?? null, fuso);
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      <Button
        {...props}
        disabled={disabled === true || bloqueado}
        aria-describedby={texto ? id : undefined}
      >
        {children}
      </Button>
      {texto && <Motivo id={id} texto={texto} oculto={Boolean(motivo) && !motivoVisivel} />}
      <span className="sr-only" aria-live="polite">
        {liberou ? avisoLiberado : ''}
      </span>
    </span>
  );
}
